import axios from 'axios';

/**
 * Returns the base URL for the backend API.
 * Dynamically resolves to the current hostname when accessed from other computers on the LAN.
 */
export const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    return envUrl;
  }
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/api/v1`;
  }
  return 'http://localhost:8000/api/v1';
};

/**
 * Returns the base URL of the backend host (without /api/v1), useful for static media/avatars.
 */
export const getServerHostUrl = (): string => {
  const apiUrl = getApiBaseUrl();
  return apiUrl.replace(/\/api\/v1\/?$/, '');
};

/**
 * Resolves a media / avatar URL to an absolute URL pointing to the correct host.
 */
export const resolveMediaUrl = (url?: string | null): string => {
  if (!url) return '/static/images/avatars/1.png';
  let processedUrl = url;
  if (processedUrl.includes('localhost:9000')) {
    processedUrl = processedUrl.replace(/^https?:\/\/localhost:9000/, '');
  }
  if (processedUrl.startsWith('http://') || processedUrl.startsWith('https://')) return processedUrl;
  const serverHost = getServerHostUrl();
  const normalizedPath = processedUrl.startsWith('/') ? processedUrl : `/${processedUrl}`;
  return `${serverHost}${normalizedPath}`;
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add a request interceptor to include the JWT token and Tenant ID
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('mentisra_token');
    const tenantId = localStorage.getItem('mentisra_tenant_id');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (tenantId && !config.headers['X-Tenant-ID']) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle unauthorized errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear storage and redirect to login if unauthorized
      localStorage.removeItem('mentisra_token');
      localStorage.removeItem('mentisra_user');
      localStorage.removeItem('mentisra_tenant_id');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default api;
